import { API } from '../shared/constants';
import {
    SchedulesTab,
    EntitySchedulesTab,
    UploadScheduleDetail,
    ScheduleFilterParameters,
    SchedulesRoutesTab,
    EntitySchedulesRoutesTab,
    ScheduleRow,
    PCNList,
    PCNFiltersParameters,
    PCNRoutes,
    PCNAssignmentUser,
    ScheduleRoutesFiltersParameters,
    EntityPCN,
    RouteGridList,
    UserTeam,
    SessionTypeParameters,
    ShiftLabelParameters,
    SchedulesV3,
    RouteFrequency
} from '../shared/domain';
import { SchedulesBaseAdapter } from './schedules-base-adapter';
import { HttpConnectorAdapter } from './http-connector-adapter';
import { JobCreationResult, JobErrorResult, JobStatusResult } from '../models/jobs';
import { PDF2JPGConvertion, PDFApiMarker } from '../shared/domain/leaflet';

interface BaseParameters extends Omit<ScheduleFilterParameters & PCNFiltersParameters, ''> {
    limit?: number;
    offset?: number;
}

let singletonInstance: SchedulesAdapter;
export class SchedulesAdapter implements SchedulesBaseAdapter {
    static getAdapter(): SchedulesBaseAdapter {
        if (!singletonInstance) {
            singletonInstance = new SchedulesAdapter();
        }

        return singletonInstance;
    }

    async getSchedules(limit: number, offset: number, filters = {} as ScheduleFilterParameters): Promise<SchedulesTab> {
        const params: BaseParameters = {};

        if (limit > 0) {
            params.limit = limit;
        }

        if (offset > 0) {
            params.offset = offset;
        }

        for (const filter in filters) {
            params[filter as keyof ScheduleFilterParameters] = filters[filter as keyof ScheduleFilterParameters];
        }

        const stringifiedParams = HttpConnectorAdapter.Utilities.stringifyGetParams(params);
        
        const response = await HttpConnectorAdapter
            .get(
                `${API.URL.SCHEDULES.SCHEDULES_BY_USER_ID}?${stringifiedParams}`
            );

        const result = SchedulesTab.create(response.data as EntitySchedulesTab);
        if (!result){
            return Promise.reject();
        }

        return result;
    }

    async submitSchedules(
        accountId: string,
        file: File,
        fileUploadDetails?: UploadScheduleDetail
    ): Promise<string> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('account_id', accountId);

        if (fileUploadDetails) {
            const {
                moduleGroupId,
                activeDate,
                expireDate
            } = fileUploadDetails;

            if (moduleGroupId) {
                formData.append('module_group_id', moduleGroupId);
            }

            if (activeDate) {
                formData.append('start_date', `${activeDate}`);
            }

            if (expireDate) {
                formData.append('expiration_date', `${expireDate}`);
            }
        }

        const headers = { 'Content-Type': 'multipart/form-data' };
        const response = await HttpConnectorAdapter
            .post(
                `${API.URL.SCHEDULES.SCHEDULES_BY_USER_ID}ingest/cbre`, formData, headers
            );

        const {
            status: responseStatus,
            data
        } : { 
            data?: {
                job_id: string;
            },
            status: boolean;
        } = response.data;

        if ((!responseStatus) || !data) {
            return Promise.reject();
        }

        const { job_id: jobId } = data;
        return Promise.resolve(jobId);
    }

    async getSubmitSchedulesStatus(jobId: string): Promise<JobStatusResult> {
        const response = await HttpConnectorAdapter
            .get(
                API.URL.SCHEDULES.JOB_STATUS_BY_JOB_ID
                    .replace(API.URL.Tokens.SCHEDULES.JOB_ID, jobId)
            );

        const { 
            status: responseStatus,
            data
        } : {
            status: boolean,
            data?: JobStatusResult
        } = response.data;

        if ((!responseStatus) || !data) {
            return Promise.reject();
        }
        return Promise.resolve(data);
    }

    async getSubmitSchedulesError(jobId: string): Promise<JobErrorResult> {
        const response = await HttpConnectorAdapter
            .get(
                API.URL.SCHEDULES.JOB_ERROR_BY_JOB_ID
                    .replace(API.URL.Tokens.SCHEDULES.JOB_ID, jobId)
            );

        const { 
            status: responseStatus,
            data
        } : {
            status: boolean,
            data?: JobErrorResult
        } = response.data;

        if ((!responseStatus) || !data) {
            return Promise.reject();
        }
        return Promise.resolve(data);
    }

    async submitSchedulesRoute(routeData: RouteGridList, secondaryIdrowId: number, userTeam?: UserTeam): Promise<JobCreationResult> {
        const teamIdHeader = userTeam ? {teamId: userTeam.teamId} : {};

        const {
            SCHEDULES: { SCHEDULE_ROUTES }
        } = API.URL;

        const body: {
            data: RouteGridList,
            secondary_id_row_id: number
        } = {
            data: routeData,
            secondary_id_row_id: secondaryIdrowId
        };

        const response = await HttpConnectorAdapter
            .post(
                SCHEDULE_ROUTES,
                body,
                teamIdHeader
            );

        const { 
            status: responseStatus,
            message,
            data
        } : {
            status: boolean,
            message: string,
            data: { job_id: string }
        } = response.data;

        if ((!responseStatus) || !data) {
            return Promise.reject();
        }

        const result: JobCreationResult = {
            status: responseStatus,
            message,
            jobId: data.job_id
        };

        return Promise.resolve(result);
    }

    async createSchedulePcn(
        pcnValue: string,
        folderId: string,
        shiftLabel: number | string,
        sessionTypeId?: number,
        userTeam?: UserTeam
    ): Promise<EntityPCN> {
        const teamIdHeader = userTeam ? {teamId: userTeam.teamId} : {};
        const body: { [key: string]: string | number } = {
            value: pcnValue,
            folder_id: folderId
        };

        if (sessionTypeId) {
            body.shift_label_id = shiftLabel as number;
            body.session_type_id = sessionTypeId;
        } else {
            body.shift_label = shiftLabel as string;
        }

        const response = await HttpConnectorAdapter
            .post(`${API.URL.SCHEDULES.PCN}`,
                body,
                teamIdHeader
            );

        const { 
            status: responseStatus,
            data: {
                value, id,
                shift_label,
                frequencies,
                default_frequency
            }
        } : {
            status: boolean,
            data: {
                value: string,
                id: number,
                shift_label: string,
                frequencies?: RouteFrequency[];
                default_frequency?: string;
            }
        } = response.data;

        if ((!responseStatus)) {
            return Promise.reject();
        }

        return Promise.resolve({
            pcnId: `${id}`,
            pcnValue: value,
            shiftLabel: shift_label,
            frequencies,
            defaultFrequency: default_frequency
        });
    }

    async editSchedulePcn(
        pcnId: string,
        folderId: string,
        pcnValue?: string,
        shiftLabel?: string | number,
        userTeam?: UserTeam
    ): Promise<JobCreationResult | string> {
        const teamIdHeader = userTeam ? {teamId: userTeam.teamId} : {};
        const body: { [key: string]: string | number } = {
            id: Number(pcnId),
            folder_id: folderId
        };

        if (shiftLabel) {
            if (typeof shiftLabel === 'number') {
                body.shift_label_id = shiftLabel as number;
            } else {
                body.shift_label = shiftLabel as string;
            }
        }

        if (pcnValue) {
            body.value = pcnValue;
        }

        const response = await HttpConnectorAdapter
            .patch(`${API.URL.SCHEDULES.PCN}${pcnId}/`, body, teamIdHeader);

        const { 
            status: responseStatus,
            message,
            data: { value, shift_label, job_id }
        } : {
            status: boolean,
            message: string,
            data: { value?: string, shift_label?: string, job_id?: string }
        } = response.data;

        if ((!responseStatus)) {
            return Promise.reject();
        }

        const result = !pcnValue ? `${message} - PCN: ${value}, Shift Label: ${shift_label}` : {
            status: responseStatus,
            message,
            jobId: job_id
        } as JobCreationResult | string;

        return Promise.resolve(result);
    }

    async deleteSchedulePcn(pcnId: string, userTeam?: UserTeam): Promise<JobCreationResult> {
        const teamIdHeader = userTeam ? {teamId: userTeam.teamId} : {};
        const response = await HttpConnectorAdapter
            .delete(`${API.URL.SCHEDULES.PCN}/${pcnId}/`, null, teamIdHeader);

        const { 
            status: responseStatus,
            message,
            data: { job_id }
        } : {
            status: boolean,
            message: string,
            data: { job_id: string }
        } = response.data;

        if ((!responseStatus)) {
            return Promise.reject();
        }

        const result: JobCreationResult = {
            status: responseStatus,
            message,
            jobId: job_id
        };

        return Promise.resolve(result);
    }

    async getUsersRoutes(): Promise<SchedulesRoutesTab> {
        const response = await HttpConnectorAdapter.get(API.URL.SCHEDULES.ROUTES);

        const result = SchedulesRoutesTab.create(response.data as EntitySchedulesRoutesTab);
        if (!result){
            return Promise.reject();
        }

        return result;
    }

    async getSchedulesRoutes(folderId: string, secondaryIdrowId: string, userTeam?: UserTeam): Promise<SchedulesV3> {
        const teamIdHeader = userTeam ? {teamId: userTeam.teamId} : {};

        const params: ScheduleRoutesFiltersParameters = {
            folder_id: folderId,
            secondary_id_row_id: secondaryIdrowId
        };

        const response = await HttpConnectorAdapter.get(`${API.URL.SCHEDULES.SCHEDULE_ROUTES}?grid_format=true`, params, teamIdHeader);
        
        const { 
            status: responseStatus,
            data
        } : {
            status: boolean,
            data: SchedulesV3
        } = response.data;

        if ((!responseStatus) || !data) {
            return Promise.reject();
        }

        return Promise.resolve(data);
    }

    async getScheduleModulesGroup(folderId: string, userTeam?: UserTeam): Promise<{[key: string]: ScheduleRow[]}> {
        const teamIdHeader = userTeam ? {teamId: userTeam.teamId} : {};

        const moduleGroups = await HttpConnectorAdapter.get(API.URL.SCHEDULES.MODULE_GROUPS
            .replace(API.URL.Tokens.SCHEDULES.FOLDER_ID, folderId), null, teamIdHeader);
        
        const {
            status: moduleGroupsStatus,
            data: moduleGroupsData
        }: {
            status: boolean,
            data: { [key: string]: ScheduleRow[] }
        } = moduleGroups.data;

        if ((!moduleGroupsStatus) || !moduleGroupsData) {
            return Promise.reject();
        }

        return Promise.resolve(moduleGroupsData);
    } 

    async addUserAssignment(
        secondaryIdrowId: number,
        userId: string,
        startTime: number,
        endTime?: number,
        timezone?: string,
        userTeam?: UserTeam
    ): Promise<string> {
        const teamIdHeader = userTeam ? {teamId: userTeam.teamId} : {};

        const response = await HttpConnectorAdapter
            .post(`${API.URL.SCHEDULES.ROUTES_ASSIGNMENT}`,
                {
                    secondary_id_row_id: secondaryIdrowId,
                    user_id: userId,
                    start_time: startTime,
                    end_time: endTime ? endTime : 99999999999,
                    timezone
                },
                teamIdHeader
            );

        const { 
            status: responseStatus,
            message,
            data: { secondary_id }
        } : {
            status: boolean,
            message: string,
            data: { secondary_id: string }
        } = response.data;

        if ((!responseStatus)) {
            return Promise.reject();
        }

        return Promise.resolve(`${message} route ${secondary_id}`);
    }

    async removeUserAssignment(secondaryId: string, userTeam?: UserTeam): Promise<string> {
        const teamIdHeader = userTeam ? {teamId: userTeam.teamId} : {};

        const response = await HttpConnectorAdapter
            .post(`${API.URL.SCHEDULES.PCN}${secondaryId}/unassign/`,
                {
                    unassignment_type: 'full'
                },
                teamIdHeader
            );

        const { 
            status: responseStatus,
            message
        } : {
            status: boolean,
            message: string
        } = response.data;

        if ((!responseStatus)) {
            return Promise.reject();
        }

        return Promise.resolve(message);
    }

    async createScheduleUserList(folderId: string, folderName: string, users: string[]): Promise<string> {
        const response = await HttpConnectorAdapter
            .post(`${API.URL.SCHEDULES.SCHEDULE_USER_LISTS}`,
                {
                    folder_doc_id: folderId,
                    name: folderName,
                    users: users
                }
            );

        const { 
            status: responseStatus,
            message,
            data: { name }
        } : {
            status: boolean,
            message: string,
            data: { name: string }
        } = response.data;

        if ((!responseStatus)) {
            return Promise.reject();
        }

        return Promise.resolve(`${message} Folder ${name}`);
    }

    async editScheduleUserList(userListId: string, folderName: string, users: string[]): Promise<string> {
        const response = await HttpConnectorAdapter
            .patch(`${API.URL.SCHEDULES.SCHEDULE_USER_LISTS}${userListId}`,
                {
                    name: folderName,
                    users: users
                }
            );

        const { 
            status: responseStatus,
            message,
            data: { name }
        } : {
            status: boolean,
            message: string,
            data: { name: string }
        } = response.data;

        if ((!responseStatus)) {
            return Promise.reject();
        }

        return Promise.resolve(`${message} Folder ${name}`);
    }

    async getPcnUserList(pcnId: string, current_one?: boolean, userTeam?: UserTeam): Promise<PCNAssignmentUser[]> {
        const teamIdHeader = userTeam ? {teamId: userTeam.teamId} : {};

        const {
            SCHEDULES: { ROUTES_ASSIGNMENT }
        } = API.URL;

        const url = `${ROUTES_ASSIGNMENT}?secondary_id_row_id=${pcnId}`;
        const response = await HttpConnectorAdapter.get(`${url}${ current_one ? '&current_one=true' : ''}`, null, teamIdHeader);
        
        const { 
            status: responseStatus,
            data
        } : {
            status: boolean,
            data: PCNAssignmentUser[]
        } = response.data;

        if ((!responseStatus) || !data) {
            return Promise.reject();
        }

        return Promise.resolve(data);
    }

    async getPCNList(
        limit: number,
        offset: number,
        filters = {} as PCNFiltersParameters,
        userTeam?: UserTeam
    ): Promise<PCNRoutes> {
        const params: BaseParameters = {};

        if (limit > 0) {
            params.limit = limit;
        }

        if (offset > 0) {
            params.offset = offset;
        }

        for (const filter in filters) {
            params[filter as keyof PCNFiltersParameters] = filters[filter as keyof PCNFiltersParameters];
        }

        const {
            SCHEDULES: { PCN }
        } = API.URL;
        const teamIdHeader = userTeam ? {teamId: userTeam.teamId} : {};
        const response = await HttpConnectorAdapter.get(`${PCN}?current_assignment=true`, params, teamIdHeader);
        
        const { 
            status: responseStatus,
            data,
            meta: { total_count }
        } : {
            status: boolean,
            data: PCNList[],
            meta: { total_count: number }
        } = response.data;

        if ((!responseStatus) || !data) {
            return Promise.reject();
        }

        return Promise.resolve({ rows: data, total: total_count });
    }

    async getMarkersCoordinates(floorplanId: string): Promise<PDFApiMarker[]> {
        const {
            MAPS: { MARKERS },
            Tokens: { 
                MAPS: { FLOORPLAN_ID }
            }
        } = API.URL;

        const response = await HttpConnectorAdapter.get(MARKERS.replace(FLOORPLAN_ID, floorplanId));

        const {
            status: responseStatus,
            data: { markers }
        } : {
            status: boolean,
            data: {
                markers: PDFApiMarker[]
            }
        } = response.data;

        if (!responseStatus || !markers) {
            return Promise.reject();
        }

        return Promise.resolve(markers);
    }

    async getFloorplanImage(floorplanId: string): Promise<PDF2JPGConvertion> {
        const {
            MAPS: { PDF_IMAGE },
            Tokens: { 
                MAPS: { FLOORPLAN_ID }
            }
        } = API.URL;

        const response = await HttpConnectorAdapter.get(PDF_IMAGE.replace(FLOORPLAN_ID, floorplanId));

        const {
            status: responseStatus,
            data
        } : {
            status: boolean,
            data: PDF2JPGConvertion
        } = response.data;

        if (!responseStatus || !data) {
            return Promise.reject();
        }

        return Promise.resolve(data);
    }

    async getSessionTypes(): Promise<SessionTypeParameters[]> {
        const {
            SCHEDULES: { SESSION_TYPES }
        } = API.URL;

        const response = await HttpConnectorAdapter.get(SESSION_TYPES);

        const {
            status: responseStatus,
            data
        } : {
            status: boolean,
            data: SessionTypeParameters[]
        } = response.data;

        if (!responseStatus || !data) {
            return Promise.reject();
        }

        return Promise.resolve(data);
    }

    async getShiftLabels(): Promise<ShiftLabelParameters[]> {
        const {
            SCHEDULES: { SHIFT_LABELS }
        } = API.URL;

        const response = await HttpConnectorAdapter.get(SHIFT_LABELS);

        const {
            status: responseStatus,
            data
        } : {
            status: boolean,
            data: ShiftLabelParameters[]
        } = response.data;

        if (!responseStatus || !data) {
            return Promise.reject();
        }

        return Promise.resolve(data);
    }
}